import React, { useEffect, useMemo, useState } from 'react'
import { describeMediaPhotos, downloadAllFullMedia, getApiErrorMessage, getModelMedia, getModelMediaFolders, getModels, syncModelMedia } from '../../api'
import MediaEditModal from '../../components/MediaEditModal'
import { EmptyState, LoadingBlock, MetricCard, Section } from '../../components/v2/V2Primitives'
import { asList, folderPayload, folderValue, modelTitle, selectedFolderLabel, totalOf } from './v2Utils'
import AdminV2MediaTile from './AdminV2MediaTile'
import { applyMediaUpdate } from '../../lib/mediaPresentation'

export default function AdminV2Content() {
  const [loading, setLoading] = useState(true)
  const [loadingMedia, setLoadingMedia] = useState(false)
  const [models, setModels] = useState([])
  const [modelId, setModelId] = useState('')
  const [folders, setFolders] = useState([])
  const [folder, setFolder] = useState('')
  const [media, setMedia] = useState([])
  const [mediaTotal, setMediaTotal] = useState(0)
  const [error, setError] = useState('')
  const [actionStatus, setActionStatus] = useState('')
  const [busy, setBusy] = useState('')
  const [editModalItem, setEditModalItem] = useState(null)
  const [showHiddenMedia, setShowHiddenMedia] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => { setEditModalItem(null); setMedia([]); setError('') }, [modelId])

  useEffect(() => {
    let alive = true
    getModels()
      .then((result) => {
        if (!alive) return
        const rows = asList(result)
        setModels(rows)
        if (rows[0]?.id != null) setModelId((current) => current || String(rows[0].id))
      })
      .catch((err) => { if (alive) setError(getApiErrorMessage(err, 'Failed to load models')) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  useEffect(() => {
    if (!modelId) return
    let alive = true
    setFolder('')
    setFolders([])
    getModelMediaFolders(modelId)
      .then((result) => { if (alive) setFolders(asList(result)) })
      .catch((err) => { if (alive) setError(getApiErrorMessage(err, 'Failed to load content sources')) })
    return () => { alive = false }
  }, [modelId])

  useEffect(() => {
    if (!modelId) return
    let alive = true
    setLoadingMedia(true)
    getModelMedia(modelId, { limit: 60, offset: 0, include_hidden: showHiddenMedia, ...folderPayload(folder) })
      .then((result) => {
        if (!alive) return
        const rows = asList(result)
        setMedia(rows)
        setMediaTotal(totalOf(result, rows.length))
      })
      .catch((err) => { if (alive) setError(getApiErrorMessage(err, 'Failed to load media')) })
      .finally(() => { if (alive) setLoadingMedia(false) })
    return () => { alive = false }
  }, [folder, modelId, showHiddenMedia, refreshKey])

  const selectedModel = useMemo(() => models.find((model) => String(model.id) === String(modelId)), [modelId, models])
  const scopeLabel = selectedFolderLabel(folders, folder)
  const downloaded = media.filter((item) => item.media_path).length
  const downloading = media.filter((item) => item.media_status === 'downloading').length

  async function runAction(kind) {
    if (!modelId) return
    setBusy(kind)
    setActionStatus('')
    try {
      if (kind === 'sync') await syncModelMedia(modelId, false, folderPayload(folder))
      if (kind === 'download') await downloadAllFullMedia(modelId, folderPayload(folder))
      if (kind === 'describe') {
        const result = await describeMediaPhotos(modelId, { ...folderPayload(folder), limit: 50 })
        setActionStatus(`Queued ${result?.queued || 0} photo descriptions for: ${scopeLabel}`)
      } else {
        setActionStatus(`${kind === 'sync' ? 'Sync' : 'Download'} queued for: ${scopeLabel}`)
      }
    } catch (err) {
      setActionStatus(getApiErrorMessage(err, 'Action failed'))
    } finally {
      setBusy('')
    }
  }

  function updateMediaItem(updated) {
    setMedia((prev) => applyMediaUpdate(prev, updated, showHiddenMedia))
    if (updated?._visibility_changed) setRefreshKey((value) => value + 1)
  }

  if (loading) return <LoadingBlock label="Loading content sources..." />

  return (
    <div className="v2-stack">
      <div className="v2-metrics-grid">
        <MetricCard label="Selected model" value={selectedModel ? modelTitle(selectedModel) : 'None'} detail="Content scope starts with model" />
        <MetricCard label="Sources" value={folders.length + 1} detail="All + provider folders" />
        <MetricCard label="Loaded media" value={media.length} detail={`${mediaTotal} in selected scope`} />
        <MetricCard label="Downloaded" value={downloaded} detail={`${downloading} currently downloading`} tone={downloading ? 'warn' : 'good'} />
      </div>

      <Section eyebrow="Content sources" title="Scoped media operations" description="Every sync and download button uses the selected source. No hidden all-folder action.">
        {error ? <div className="v2-error">{error}</div> : null}
        <div className="v2-content-layout">
          <aside className="v2-source-panel">
            <label>
              <span>Model</span>
              <select value={modelId} onChange={(e) => setModelId(e.target.value)}>
                {models.map((model) => <option key={model.id} value={model.id}>{modelTitle(model)}</option>)}
              </select>
            </label>
            <SourceList folders={folders} folder={folder} setFolder={setFolder} mediaTotal={mediaTotal} />
          </aside>

          <main className="v2-media-workspace">
            <div className="v2-scope-banner">
              <div>
                <p className="v2-kicker">Selected scope</p>
                <h3>{scopeLabel}</h3>
                <p>{selectedModel ? modelTitle(selectedModel) : 'No model selected'} - {mediaTotal} media in scope</p>
              </div>
              <div className="v2-scope-actions">
                <label className="v2-inline-check">
                  <input type="checkbox" checked={showHiddenMedia} onChange={(event) => setShowHiddenMedia(event.target.checked)} />
                  Show hidden
                </label>
                <button type="button" className="v2-btn v2-btn-primary" disabled={!!busy || !modelId} onClick={() => runAction('sync')}>
                  {busy === 'sync' ? 'Queueing...' : 'Sync selected source'}
                </button>
                <button type="button" className="v2-btn v2-btn-secondary" disabled={!!busy || !modelId} onClick={() => runAction('download')}>
                  {busy === 'download' ? 'Queueing...' : 'Download selected source'}
                </button>
                <button type="button" className="v2-btn v2-btn-secondary" disabled={!!busy || !modelId} onClick={() => runAction('describe')}>
                  {busy === 'describe' ? 'Queueing...' : 'Describe photos'}
                </button>
              </div>
            </div>
            {actionStatus ? <div className="v2-info">{actionStatus}</div> : null}

            {loadingMedia ? <LoadingBlock label="Loading selected media..." /> : media.length ? (
              <div className="v2-media-grid">
                {media.map((item) => (
                  <AdminV2MediaTile key={`${modelId}:${item.id}`} modelId={modelId} item={item} onEdit={setEditModalItem} onLibrarySaved={updateMediaItem} />
                ))}
              </div>
            ) : (
              <EmptyState title="No media loaded for this source" description="Sync the selected source or choose another folder." />
            )}
          </main>
        </div>
      </Section>

      <MediaEditModal
        open={!!editModalItem}
        modelId={modelId}
        item={editModalItem}
        onClose={() => setEditModalItem(null)}
        onSaved={updateMediaItem}
      />
    </div>
  )
}

function SourceList({ folders, folder, setFolder, mediaTotal }) {
  return (
    <div className="v2-source-list">
      <button type="button" className={!folder ? 'is-active' : ''} onClick={() => setFolder('')}>
        <strong>All content sources</strong>
        <span>{mediaTotal || 'All media'}</span>
      </button>
      {folders.map((item) => (
        <button key={folderValue(item)} type="button" className={folder === folderValue(item) ? 'is-active' : ''} onClick={() => setFolder(folderValue(item))}>
          <strong>{item.name || 'Unnamed source'}</strong>
          <span>{typeof item.count === 'number' ? `${item.count} media` : 'Provider source'}</span>
        </button>
      ))}
    </div>
  )
}
